import { render } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { getBreakpointMediaQuery } from 'js/constants.js';
import { QRCode } from '../../../../product-new/clientlibs/site/js/qrcode.js';
import CustomSelect from './custom-select';
import OverviewSection from './overview-section';
import ComponentsSection from './components-section';
import ComponentLevelSpecificationsSection from './components-spec-section';
import DocumentSection from './document-section';
import CertificationSection from './certification-section';
import EtoHeader from './eto-header';
import EtoTabs from './eto-tabs';
import InsertFragment from './insert-fragment';
import GenericSpecificationSection from './generic-spec-section';

const componentName = 'eto-app';

export interface Product {
    id: string;
    name: string;
    description: string;
    longDescription: string;
    images: { url: string; alt: string }[];
    quantity: number;
    lifeCycleStatus: string;
    link: string;
    preferred: boolean;
    characteristics: ProductCharacteristic[];
    hasDocuments: boolean;
    hasCertifications: boolean;
    lastUpdated: Date;
}

export interface Document {
    id: string;
    name: string;
    type: string;
    publicationNumber: string;
    link: string;
    componentIds: string[];
}

export interface Certification {
    name: string;
    link: string;
    icon: string;
    componentIds: string[];
}

export interface ProductCharacteristic {
    name: string;
    value: string;
    classCode: string;
    section: string;
    component: string;
    componentName: string;
}

export interface ComponentSpecifications {
    components: {
        componentId: string;
        componentName: string;
        sections: {
            sectionName: string;
            characteristics: ProductCharacteristic[];
        }[];
    }[];
}

export interface GenericSpecification {
    tab: string;
    tabId: string;
    subtab: string;
    subtabId: string;
    section: string;
    sectionId: string;
    subsection: string;
    subsectionId: string;
    cas: string;
    name: string;
    value: string;
}

export interface Distributor {
    doc: any;
    aprCode: string;
    partnerId: string;
    partnerType: string;
    distributorName: string;
    partnerName: string;
    partnerEmail: string;
    partnerPhone: string;
    city: string;
    partnerAddress: string;
    image: string;
    url: string;
}

export interface TabSection {
    name: string;
    label: string;
    subtabs: TabSection[];
    sections: { name: string; label: string }[];
    activeSection: string;
    hidden: boolean;
    collapseSubtabs: boolean;
}

export interface InsertFragmentData {
    tab: string;
    subtab: string;
    position: 'top' | 'bottom';
    contentPath: string;
}

function EtoApp() {
    const PRODUCT_SERVLET_URL = '/bin/rockwell-automation/product-details';
    const DEFAULT_IMAGE = '/etc.clientlibs/rockwell-aem-base/clientlibs/clientlib-base/resources/icons/image_unavailable.svg';

    const euImporterAddress = document.querySelector<HTMLElement>(`.${componentName}__metadata .${componentName}__eu-importer-address`)?.innerHTML;
    const euRepresentativeAddress = document.querySelector<HTMLElement>(`.${componentName}__metadata .${componentName}__eu-representative-address`)?.innerHTML;
    const classCodeMapping: Record<string, string[]>[] = [];
    [...document.querySelectorAll<HTMLElement>(`.${componentName}__metadata .${componentName}__class-code-mapping`)].forEach((characteristicSection) => {
        const section = characteristicSection.getAttribute('data-section');
        if (!section) {
            return;
        }
        const classCodes = [...characteristicSection.querySelectorAll<HTMLElement>(`.${componentName}__class-code-mapping-item`)].filter((item) => item.getAttribute('data-class-code') !== null).map((item) => item.getAttribute('data-class-code') || '');
        if (classCodes.length === 0) {
            return;
        }
        const classCodeMappingItem: Record<string, string[]> = {};
        classCodeMappingItem[section] = classCodes;
        classCodeMapping.push(classCodeMappingItem);
    });
    const tabSorting: Record<string, string[]>[] = [];
    [...document.querySelectorAll<HTMLElement>(`.${componentName}__metadata .${componentName}__tab-sorting`)].forEach((tabSortingSection) => {
        const tab = tabSortingSection.getAttribute('data-tab');
        if (!tab) {
            return;
        }
        const subtabs = [...tabSortingSection.querySelectorAll<HTMLElement>(`.${componentName}__subtab-sorting-item`)].filter((item) => item.getAttribute('data-subtab') !== null).map((item) => item.getAttribute('data-subtab') || '');
        const tabSortingItem: Record<string, string[]> = {};
        tabSortingItem[tab] = subtabs;
        tabSorting.push(tabSortingItem);
    });
    const hiddenTabs = [...document.querySelectorAll<HTMLElement>(`.${componentName}__metadata .${componentName}__hidden-tabs`)].map((item) => item.getAttribute('data-hidden-tab') || '');
    const hideChemicalProperties = document.querySelector<HTMLElement>(`.${componentName}__metadata .${componentName}__hide-chemical-properties`)?.getAttribute('data-hide-chemical-properties') === 'true';
    const lifecycleAssessmentDisclaimerText = document.querySelector<HTMLElement>(`.${componentName}__metadata .${componentName}__lifecycle-assessment-disclaimer-text`)?.innerHTML;
    const certificationTypes = [...document.querySelectorAll<HTMLElement>(`.${componentName}__metadata .${componentName}__certification-type`)].map((item) => ({
        value: item.getAttribute('data-certification-type') || '',
        icon: item.getAttribute('data-certification-icon') || '',
    }));
    const serialNumberTooltipText = document.querySelector<HTMLElement>(`.${componentName}__metadata .${componentName}__serial-number-tooltip-text`)?.innerHTML || '';
    const afDemoDisclaimerText = document.querySelector<HTMLElement>(`.${componentName}__metadata .${componentName}__af-demo-disclaimer-text`)?.innerHTML || '';
    const badgeOverrides = [...document.querySelectorAll<HTMLElement>(`.${componentName}__metadata .${componentName}__af-demo-badge`)].map((item) => ({
        icon: item.getAttribute('data-badge-icon') || '',
        title: item.getAttribute('data-badge-name') || '',
        code: item.getAttribute('data-badge-code') || '',
    }));

    const salesOrderNumber = new URLSearchParams(window.location.search).get('.S')?.split('-')[0];
    const salesOrderLineItemNumber = new URLSearchParams(window.location.search).get('.S')?.split('-').slice(1).join('-');
    const serialNumber = new URLSearchParams(window.location.search).get('.S') || '';
    const proxy = new URLSearchParams(window.location.search).get('proxy');
    const locale = document.documentElement.lang.toLowerCase();
    const language = locale.split('-')[0] || 'en';
    const countryCode = locale.split('-')[1] || 'us';
    let regionLanguage = 'na/en';
    if (document.body.dataset.pagePath && document.body.dataset.pagePath.startsWith('/content/rockwell-automation/global/')) {
        const region = document.body.dataset.pagePath.split('/content/rockwell-automation/global/')[1].split('/')[0];
        const language = document.body.dataset.pagePath.split(`/content/rockwell-automation/global/${region}/`)[1].split('/')[0];
        if (region && language) {
            regionLanguage = `${region}/${language}`;
        }
    }

    const i18nDictionaryRef = useRef<Record<string, string>>({});
    const [i18nReady, setI18nReady] = useState(false);
    
    // AF Demo Data
    const [insertFragments, setInsertFragments] = useState<InsertFragmentData[]>([]);
    const [brandLogoOverride, setBrandLogoOverride] = useState<string | null>(null);
    const [demoSpecifications, setDemoSpecifications] = useState<GenericSpecification[] | null>(null);
    const [productDescriptionOverride, setProductDescriptionOverride] = useState<string | null>(null);
    const [demoImages, setDemoImages] = useState<{ url: string; alt: string }[]>([]);
    const [demoBadges, setDemoBadges] = useState<{ icon: string; title: string, code: string, position: string }[]>([]);
    const [demoLinks, setDemoLinks] = useState<{ icon: string; title: string; url: string, position: string }[]>([]);

    const sectionSelectors = {
        overview: 'overview',
        components: 'components',
        technicalSpecifications: 'technical-specifications',
        componentLevelSpecifications: 'component-level-specifications',
        documents: 'documents',
        certifications: 'certifications',
    };
    const sectionTabsObject: TabSection[] = [];
    sectionTabsObject.push({
        name: sectionSelectors.overview,
        label: 'Overview',
        subtabs: [],
        sections: [],
        activeSection: '',
        hidden: hiddenTabs.includes('Overview'),
        collapseSubtabs: false,
    });
    sectionTabsObject.push({
        name: sectionSelectors.components,
        label: 'Components',
        subtabs: [],
        sections: [],
        activeSection: '',
        hidden: hiddenTabs.includes('Components'),
        collapseSubtabs: false,
    });
    sectionTabsObject.push({
        name: sectionSelectors.technicalSpecifications,
        label: 'Technical Specifications',
        subtabs: [],
        sections: [],
        activeSection: '',
        hidden: hiddenTabs.includes('Technical Specifications'),
        collapseSubtabs: false,
    });
    sectionTabsObject.find((tab) => tab.name === sectionSelectors.technicalSpecifications)?.subtabs.push({
        name: sectionSelectors.componentLevelSpecifications,
        label: 'Component-level Specifications',
        subtabs: [],
        sections: [],
        activeSection: 'All',
        hidden: hiddenTabs.includes('Component-level Specifications'),
        collapseSubtabs: false,
    });
    sectionTabsObject.push({
        name: sectionSelectors.documents,
        label: 'Documents',
        subtabs: [],
        sections: [],
        activeSection: 'All',
        hidden: hiddenTabs.includes('Documents'),
        collapseSubtabs: false,
    });
    sectionTabsObject.push({
        name: sectionSelectors.certifications,
        label: 'Certifications',
        subtabs: [],
        sections: [],
        activeSection: 'All',
        hidden: hiddenTabs.includes('Certifications'),
        collapseSubtabs: false,
    });
    sectionTabsObject.sort((a, b) => {
        const aIndex = tabSorting.findIndex((tabSortingItem) => tabSortingItem[a.label]);
        const bIndex = tabSorting.findIndex((tabSortingItem) => tabSortingItem[b.label]);
        return aIndex - bIndex;
    });
    sectionTabsObject.forEach((tab) => {
        tabSorting.forEach((tabSortingItem) => {
            if (tabSortingItem[tab.label]) {
                tab.subtabs.sort((a, b) => {
                    const aIndex = tabSortingItem[tab.label].indexOf(a.label);
                    const bIndex = tabSortingItem[tab.label].indexOf(b.label);
                    return aIndex - bIndex;
                });
            }
        });
    });
    const [sectionTabs, setSectionTabs] = useState<TabSection[]>(sectionTabsObject);
    const [selectedDistributor, setSelectedDistributor] = useState<Distributor | null>(null);

    const [componentsLoading, setComponentsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState(null);

    const [pageNumber, setPageNumber] = useState(new URLSearchParams(window.location.search).get('pageNumber') ? parseInt(new URLSearchParams(window.location.search).get('pageNumber') || '1') : 1);
    const [pageSize, setPageSize] = useState(new URLSearchParams(window.location.search).get('pageSize') ? parseInt(new URLSearchParams(window.location.search).get('pageSize') || '24') : 24);
    const [totalPages, setTotalPages] = useState(0);

    const [etoProduct, setEtoProduct] = useState<Product | null>(null);
    const [components, setComponents] = useState<Product[]>([]);
    const [componentSpecifications, setComponentSpecifications] = useState<ComponentSpecifications | null>(null);
    const [documents, setDocuments] = useState<Record<string, Document[]>>({});
    const [certifications, setCertifications] = useState<Certification[]>([]);
    const [brandIcon, setBrandIcon] = useState<string | null>(null);

    const visibleSections = sectionTabsObject.filter((tab) => !tab.hidden);
    let initialSection = visibleSections.length > 0 ? (visibleSections[0].subtabs.length > 0 ? visibleSections[0].name + '__' + visibleSections[0].subtabs[0].name : visibleSections[0].name) : '';
    const [activeSection, setActiveSection] = useState(initialSection);
    // [LEGACY BREAKPOINT: min-width 1281px -> getBreakpointMediaQuery('up', 'large')]
    const desktopMediaQuery = getBreakpointMediaQuery('up', 'large');
    // [LEGACY BREAKPOINT: max-width 1023px -> getBreakpointMediaQuery('down', 'medium')]
    const mobileMediaQuery = getBreakpointMediaQuery('down', 'medium');
    const [desktopView, setDesktopView] = useState(desktopMediaQuery.matches);
    const [mobileView, setMobileView] = useState(mobileMediaQuery.matches);
    const [activeTooltip, setActiveTooltip] = useState<string | null>(null);

    // AF Demo Data
    const [hasAfDemoData, setHasAfDemoData] = useState(false);

    window.addEventListener('resize', () => {
        if (desktopMediaQuery.matches && !desktopView) {
            setDesktopView(true);
            if (!activeSection) {
                setActiveSection(visibleSections[0].subtabs.length > 0 ? visibleSections[0].name + '__' + visibleSections[0].subtabs[0].name : visibleSections[0].name);
            }
        } else if (!desktopMediaQuery.matches && desktopView) {
            setDesktopView(false);
        }
        if (mobileMediaQuery.matches && !mobileView) {
            setMobileView(true);
        } else if (!mobileMediaQuery.matches && mobileView) {
            setMobileView(false);
        }
    });

    const getEtoInfo = async () => {
        const response = await fetch(PRODUCT_SERVLET_URL + `?salesOrderNumber=${salesOrderNumber}&salesOrderLineItemNumber=${salesOrderLineItemNumber}`);
        const responseData = await response.json();
        setData(responseData);
        if (responseData.docs && responseData.docs.length > 0) {
            const etoProductData = responseData.docs[0];
            const etoId = etoProductData.id;
            if (proxy !== etoId) {
                setError('Invalid ETO ID');
                setEtoProduct(null);
                redirectToHardwarePage();
                return;
            }
            setEtoProduct({
                id: etoId,
                name: getTranslatableProductField(etoProductData, 'names', 'names_value', etoProductData.id, language),
                description: getTranslatableProductField(etoProductData, 'description', 'id', etoProductData.id, language),
                longDescription: getTranslatableProductField(etoProductData, 'eto_description', 'eto_description_value', '', language),
                images: etoProductData.galleryImages?.map((image: any) => ({ url: image.url, alt: image.altText })) || [{ url: DEFAULT_IMAGE, alt: i18n('Product Image') }],
                quantity: 1,
                lifeCycleStatus: etoProductData.productLifeCycleStatus || 'Active',
                characteristics: [],
                link: generateProductLink(etoProductData.id),
                preferred: false,
                hasDocuments: false,
                hasCertifications: false,
                lastUpdated: etoProductData.updated_date_time ? new Date(etoProductData.updated_date_time) : new Date(),
            });
            let componentDocs = [];
            if (etoProductData.bill_of_materials && etoProductData.bill_of_materials instanceof Array) {
                parseComponentSpecifications(responseData.docs[0].bill_of_materials);
                const componentCodes = etoProductData.bill_of_materials.map((item: any) => item.component_material_id).join(',');
                const componentResponse = await fetch(PRODUCT_SERVLET_URL + `?componentCodes=${componentCodes}&countryCode=${countryCode}`);
                const componentData = await componentResponse.json();
                componentDocs = componentData.docs;
                setComponents(componentDocs.filter((item: any) => !item.serialNumber || item.serialNumber.toLowerCase() === serialNumber.toLowerCase()).map((item: any, index: number) => ({
                    id: item.catalogNumber,
                    name: getTranslatableProductField(item, 'names', 'names_value', item.catalogNumber, language),
                    description: getTranslatableProductField(item, 'Rdescription', 'description', getTranslatableProductField(item, 'technicalDescription', 'technicalDescription_value', '', language), language),
                    longDescription: getTranslatableProductField(item, 'technicalDescription', 'technicalDescription_value', '', language),
                    images: [{ url: item.picture_url || DEFAULT_IMAGE, alt: i18n('Product Image') }],
                    quantity: responseData.docs[0].bill_of_materials[index].component_per_parent_quantity || 1,
                    lifeCycleStatus: item.productLifeCycleStatus || 'Active',
                    characteristics: [],
                    link: generateProductLink(item.catalogNumber),
                    preferred: ((Object.hasOwn(item, `salesOrg_${countryCode.toLowerCase()}_preferredAvailability`) && item[`salesOrg_${countryCode.toLowerCase()}_preferredAvailability`]) || (!Object.hasOwn(item, `salesOrg_${countryCode.toLowerCase()}_preferredAvailability`) && item.salesOrg_us_preferredAvailability)),
                    hasDocuments: item.documents && item.documents.length > 0,
                    hasCertifications: item.certifications && item.certifications.length > 0,
                    lastUpdated: item.lastModifiedDate ? new Date(item.lastModifiedDate) : new Date(),
                })));
                setTotalPages(Math.ceil(componentDocs.length / pageSize));
                if (pageNumber > Math.ceil(componentDocs.length / pageSize) || pageNumber < 1) {
                    setPageNumber(1);
                }
                if (componentDocs.length === 0) {
                    const componentsTab = sectionTabs.find((tab) => tab.name === sectionSelectors.components);
                    if (componentsTab) {
                        const newSectionTabs = [...sectionTabs];
                        const newComponentsTab = newSectionTabs.find((tab) => tab.name === sectionSelectors.components);
                        if (newComponentsTab) {
                            newComponentsTab.hidden = true;
                        }
                        const firstVisibleSection = newSectionTabs.find((tab) => !tab.hidden);
                        if (firstVisibleSection) {
                            initialSection = firstVisibleSection.subtabs.length > 0 ? firstVisibleSection.name + '__' + firstVisibleSection.subtabs[0].name : firstVisibleSection.name;
                        }
                        setSectionTabs(newSectionTabs);
                    }
                }
                const brand = etoProductData.brand;
                switch (brand) {
                    case "allen-bradley":
                        setBrandIcon("AllenBradley_Logo_rgb.svg");
                    case "factorytalk":
                        setBrandIcon("FactoryTalk_Logo_rgb.svg");
                    case "cubic":
                        setBrandIcon("RA-CUBIC-logo-color.svg");
                    case "rockwell automation":
                        setBrandIcon("ra.svg");
                    case "rockwell software":
                        setBrandIcon("FactoryTalk_Logo_rgb.svg");
                    case "ics triplex":
                    case "anorad":
                    case "odos imaging":
                        setBrandIcon("RA_Logo_Bug-LeftText_rgb.svg");
                    default:
                        setBrandIcon("RA_Logo_Bug-LeftText_rgb.svg");
                }
            } else {
                setComponents([]);
                const componentsTab = sectionTabs.find((tab) => tab.name === sectionSelectors.components);
                if (componentsTab) {
                    const newSectionTabs = [...sectionTabs];
                    const newComponentsTab = newSectionTabs.find((tab) => tab.name === sectionSelectors.components);
                    if (newComponentsTab) {
                        newComponentsTab.hidden = true;
                    }
                    const firstVisibleSection = newSectionTabs.find((tab) => !tab.hidden);
                    if (firstVisibleSection) {
                        initialSection = firstVisibleSection.subtabs.length > 0 ? firstVisibleSection.name + '__' + firstVisibleSection.subtabs[0].name : firstVisibleSection.name;
                    }
                    setSectionTabs(newSectionTabs);
                }
            }
            parseDocuments(componentDocs, etoProductData.documents);
            parseCertifications(componentDocs, etoProductData.certifications);
            if (etoProductData.af_flag) {
                setHasAfDemoData(true);
                if (etoProductData.af_data) {
                    parseDemoData(etoProductData.af_data);
                }
            }
        } else {
            setError('Failed to get ETO information');
            setEtoProduct(null);
            redirectToHardwarePage();
            return;
        }
    };

    const parseComponentSpecifications = (billOfMaterials: any[]) => {
        const componentCharacteristics: ProductCharacteristic[] = [];
        billOfMaterials.forEach((item: any) => {
            const name = getTranslatableProductField(item, 'char_description_name', 'names_value', '', language);
            let value = '';
            if (item.string_value) {
                value = item.string_value;
            } else if (item.date_value) {
                value = new Date(item.date_value).toLocaleDateString();
            } else if (item.decimal_value) {
                value = `${item.decimal_value}`;
            }
            if (!name || !value) {
                return;
            }
            if (item.serialNumber && item.serialNumber.toLowerCase() !== serialNumber.toLowerCase()) {
                return;
            }
            const classCode = item.classCode || '';
            let modelName = '';
            if (classCode.endsWith('MODEL_NAME')) {
                modelName = value;
            }
            let section = getTranslatableProductField(item, 'translation_section', 'section', i18n('Other'), language);
            classCodeMapping.forEach((classCodeMappingItem) => {
                Object.keys(classCodeMappingItem).forEach((key) => {
                    if (classCodeMappingItem[key].includes(classCode)) {
                        section = key;
                        return;
                    }
                });
            });
            const component = item.component_material_id;
            const characteristic = {
                name: name,
                value: value,
                classCode: classCode,
                section: section,
                component: component,
                componentName: modelName,
            };
            if (characteristic.section !== 'Hidden') {
                componentCharacteristics.push(characteristic);
            }
        });
        const componentSpecificationsObject: ComponentSpecifications = {
            components: [],
        };
        componentCharacteristics.forEach((characteristic) => {
            if (!componentSpecificationsObject.components.find((component) => component.componentId === characteristic.component)) {
                componentSpecificationsObject.components.push({
                    componentId: characteristic.component,
                    componentName: '',
                    sections: [],
                });
            }
            const componentSpec = componentSpecificationsObject.components.find((component) => component.componentId === characteristic.component);
            if (characteristic.componentName && componentSpec) {
                componentSpec.componentName = characteristic.componentName;
                return;
            }
            if (!componentSpec?.sections.find((section) => section.sectionName === characteristic.section)) {
                componentSpec?.sections.push({
                    sectionName: characteristic.section,
                    characteristics: [],
                });
            }
            componentSpec?.sections.find((section) => section.sectionName === characteristic.section)?.characteristics.push(characteristic);
        });
        const techSpecTab = sectionTabs.find((tab) => tab.name === sectionSelectors.technicalSpecifications);
        if (techSpecTab && Array.isArray(techSpecTab.subtabs)) {
            const compLevelSpecSubtab = techSpecTab.subtabs.find((subtab) => subtab.name === sectionSelectors.componentLevelSpecifications);
            if (compLevelSpecSubtab) {
                compLevelSpecSubtab.sections = componentSpecificationsObject.components.map((component: any) => ({
                    name: component.componentName || component.componentId,
                    label: getComponentLabel(component.componentName) || component.componentId,
                }));
            }
        }

        // sort componentSpecificationsObject.components.sections by index in classCodeMapping and 'Other' last
        componentSpecificationsObject.components.forEach((component) => {
            component.sections.sort((a, b) => {
                const aIndex = classCodeMapping.findIndex((classCodeMappingItem) => classCodeMappingItem[a.sectionName]);
                const bIndex = classCodeMapping.findIndex((classCodeMappingItem) => classCodeMappingItem[b.sectionName]);
                if (aIndex !== -1 && bIndex !== -1) {
                    return aIndex - bIndex;
                } else if (aIndex !== -1) {
                    return -1;
                } else if (bIndex !== -1) {
                    return 1;
                } else {
                    return a.sectionName.localeCompare(b.sectionName);
                }
            });

            // Sort data in each section by index in classCodeMapping and all other data last and alphabetically
            component.sections.forEach((section) => {
                section.characteristics.sort((a, b) => {
                    const aIndex = classCodeMapping.findIndex((classCodeMappingItem) => classCodeMappingItem[a.section]) === -1 ? -1 : classCodeMapping[classCodeMapping.findIndex((classCodeMappingItem) => classCodeMappingItem[a.section])][a.section].indexOf(a.classCode);
                    const bIndex = classCodeMapping.findIndex((classCodeMappingItem) => classCodeMappingItem[b.section]) === -1 ? -1 : classCodeMapping[classCodeMapping.findIndex((classCodeMappingItem) => classCodeMappingItem[b.section])][b.section].indexOf(b.classCode);
                    if (aIndex !== -1 && bIndex !== -1) {
                        return aIndex - bIndex;
                    } else if (aIndex !== -1) {
                        return -1;
                    } else if (bIndex !== -1) {
                        return 1;
                    } else {
                        return a.name.localeCompare(b.name);
                    }
                });
            });
        });
    
        if (componentSpecificationsObject.components.length < 2) {
            const componentLevelSpecSubtab = sectionTabs.find((tab) => tab.name === sectionSelectors.technicalSpecifications)?.subtabs.find((subtab) => subtab.name === sectionSelectors.componentLevelSpecifications);
            if (componentLevelSpecSubtab) {
                const newSectionTabs = [...sectionTabs];
                const newComponentLevelSpecSubtab = newSectionTabs.find((tab) => tab.name === sectionSelectors.technicalSpecifications)?.subtabs.find((subtab) => subtab.name === sectionSelectors.componentLevelSpecifications);
                if (newComponentLevelSpecSubtab) {
                    newComponentLevelSpecSubtab.hidden = true;
                }
                const firstVisibleSection = newSectionTabs.find((tab) => !tab.hidden);
                if (firstVisibleSection) {
                    initialSection = firstVisibleSection.subtabs.length > 0 ? firstVisibleSection.name + '__' + firstVisibleSection.subtabs[0].name : firstVisibleSection.name;
                }
                setSectionTabs(newSectionTabs);
            }
        }
        if (componentSpecificationsObject.components.length < 1) {
            const techSpecTab = sectionTabs.find((tab) => tab.name === sectionSelectors.technicalSpecifications);
            if (techSpecTab) {
                const newSectionTabs = [...sectionTabs];
                const newTechSpecTab = newSectionTabs.find((tab) => tab.name === sectionSelectors.technicalSpecifications);
                if (newTechSpecTab) {
                    newTechSpecTab.hidden = true;
                }
                const firstVisibleSection = newSectionTabs.find((tab) => !tab.hidden);
                if (firstVisibleSection) {
                    initialSection = firstVisibleSection.subtabs.length > 0 ? firstVisibleSection.name + '__' + firstVisibleSection.subtabs[0].name : firstVisibleSection.name;
                }
                setSectionTabs(newSectionTabs);
            }
        }
        setComponentSpecifications(componentSpecificationsObject);
    };

    const parseDocuments = (components: any[], demoDocuments: any[]) => {
        const documentsObject: Record<string, Document[]> = {};
        // components.forEach((component) => {
        //     if (component.documents) {
        //         component.documents.forEach((document: any) => {
        //             const documentSerialNumber = document.serialNumber;
        //             if (documentSerialNumber && documentSerialNumber.toLowerCase() !== serialNumber.toLowerCase()) {
        //                 return;
        //             }
        //             const documentType = toTitleCase(document.documentType.replaceAll('_', ' ').toLowerCase());
        //             if (!documentsObject[documentType]) {
        //                 documentsObject[documentType] = [];
        //             }
        //             if (!documentsObject[documentType].find((doc: Document) => (doc.link === document.url))) {
        //                 documentsObject[documentType].push({
        //                     id: document.documentId,
        //                     name: getTranslatableProductField(document, 'name', 'names_value', '', language),
        //                     type: documentType,
        //                     publicationNumber: document.publicationNumber || '--',
        //                     link: document.url,
        //                     componentIds: [component.catalogNumber],
        //                 } as Document);
        //             } else {
        //                 documentsObject[documentType].find((doc: Document) => (doc.link === document.url))?.componentIds.push(component.catalogNumber);
        //             }
        //         });
        //     }
        // });
        if (demoDocuments && Array.isArray(demoDocuments)) {
            demoDocuments.forEach((document: any) => {
                const includeCountries = document.includeCountryCodes;
                const excludeCountries = document.excludeCountryCodes;
                if (includeCountries && includeCountries.length > 0 && !includeCountries.includes(countryCode)) {
                    return;
                }
                if (excludeCountries && excludeCountries.length > 0 && excludeCountries.includes(countryCode)) {
                    return;
                }
                if (!documentsObject[document.documentType]) {
                    documentsObject[document.documentType] = [];
                }
                if (!documentsObject[document.documentType].find((doc: Document) => (doc.link === document.url))) {
                    documentsObject[document.documentType].push({
                        id: document.name_value,
                        name: getTranslatableProductField(document, 'name', 'name_value', '', language),
                        type: document.documentType,
                        publicationNumber: document.publicationNumber,
                        link: document.url,
                        componentIds: ['All'],
                    });
                }
            });
        }
        if (Object.keys(documentsObject).length > 0) {
            setDocuments(documentsObject);
        } else {
            const documentsTab = sectionTabs.find((tab) => tab.name === sectionSelectors.documents);
            if (documentsTab) {
                const newSectionTabs = [...sectionTabs];
                const newDocumentsTab = newSectionTabs.find((tab) => tab.name === sectionSelectors.documents);
                if (newDocumentsTab) {
                    newDocumentsTab.hidden = true;
                }
                const firstVisibleSection = newSectionTabs.find((tab) => !tab.hidden);
                if (firstVisibleSection) {
                    initialSection = firstVisibleSection.subtabs.length > 0 ? firstVisibleSection.name + '__' + firstVisibleSection.subtabs[0].name : firstVisibleSection.name;
                }
                setSectionTabs(newSectionTabs);
            }
        }
    };

    const parseCertifications = (components: any[], demoCertifications: any[]) => {
        const certificationsArray: Certification[] = [];
        // components.forEach((component) => {
        //     if (component.certifications) {
        //         component.certifications.forEach((certification: any) => {
        //             if (certification.isDeletedFlag) {
        //                 return;
        //             }
        //             const certificationSerialNumber = certification.serialNumber;
        //             if (certificationSerialNumber && certificationSerialNumber.toLowerCase() !== serialNumber.toLowerCase()) {
        //                 return;
        //             }
        //             const certificationName = getTranslatableProductField(certification, 'name', 'name_value', '', language);
        //             const link = certification.link;
        //             const certificationType = certification.certificationType;
        //             let icon = '';
        //             if (certificationTypes.find((type) => type.value === certificationType)) {
        //                 icon = `/etc.clientlibs/rockwell-aem-base/clientlibs/clientlib-base/resources/icons/${certificationTypes.find((type) => type.value === certificationType)?.icon}.svg`;
        //             }
        //             if (!certificationsArray.find((certification) => certification.name === certificationName)) {
        //                 certificationsArray.push({
        //                     name: certificationName,
        //                     link: link,
        //                     icon: icon,
        //                     componentIds: [component.catalogNumber],
        //                 });
        //             } else {
        //                 certificationsArray.find((certification) => certification.name === certificationName)?.componentIds.push(component.catalogNumber);
        //             }
        //         });
        //     }
        // });
        if (demoCertifications && Array.isArray(demoCertifications)) {
            demoCertifications.forEach((certification: any) => {
                const includeCountries = certification.includeCountryCodes;
                const excludeCountries = certification.excludeCountryCodes;
                if (includeCountries && includeCountries.length > 0 && !includeCountries.includes(countryCode)) {
                    return;
                }
                if (excludeCountries && excludeCountries.length > 0 && excludeCountries.includes(countryCode)) {
                    return;
                }
                const certificationType = certification.certificationType;
                let icon = '';
                if (certificationTypes.find((type) => type.value === certificationType)) {
                    icon = `/etc.clientlibs/rockwell-aem-base/clientlibs/clientlib-base/resources/icons/${certificationTypes.find((type) => type.value === certificationType)?.icon}.svg`;
                }
                const certificationName = getTranslatableProductField(certification, 'name', 'name_value', '', language);
                const link = certification.link;
                if (!certificationsArray.find((cert) => cert.name === certificationName)) {
                    certificationsArray.push({
                        name: certificationName,
                        link: link,
                        icon: icon,
                        componentIds: ['All'],
                    });
                }
            });
        }
        if (certificationsArray.length > 0) {
            setCertifications(certificationsArray);
        } else {
            const certificationsTab = sectionTabs.find((tab) => tab.name === sectionSelectors.certifications);
            if (certificationsTab) {
                const newSectionTabs = [...sectionTabs];
                const newCertificationsTab = newSectionTabs.find((tab) => tab.name === sectionSelectors.certifications);
                if (newCertificationsTab) {
                    newCertificationsTab.hidden = true;
                }
                const firstVisibleSection = newSectionTabs.find((tab) => !tab.hidden);
                if (firstVisibleSection) {
                    initialSection = firstVisibleSection.subtabs.length > 0 ? firstVisibleSection.name + '__' + firstVisibleSection.subtabs[0].name : firstVisibleSection.name;
                }
                setSectionTabs(newSectionTabs);
            }
        }

    };

    const parseDemoData = async (demoData: any) => {
        const insertFragmentsArray: InsertFragmentData[] = [];
        if (demoData.component && Array.isArray(demoData.component)) {
            for (let i = 0; i < demoData.component.length; i++) {
                const component = demoData.component[i];
                const includeCountries = component.includeCountryCodes;
                const excludeCountries = component.excludeCountryCodes;
                if (includeCountries && includeCountries.length > 0 && !includeCountries.includes(countryCode)) {
                    return;
                }
                if (excludeCountries && excludeCountries.length > 0 && excludeCountries.includes(countryCode)) {
                    return;
                }
                const contentPath = await checkForOutOfLocaleXf(component.componentPath)
                insertFragmentsArray.push({
                    tab: component.tab ? toKebabCase(component.tab) : component.tab,
                    subtab: component['sub-tab'] ? toKebabCase(component['sub-tab']) : component['sub-tab'],
                    position: component.position,
                    contentPath: contentPath,
                });
            }
            setInsertFragments(insertFragmentsArray);
        }
        if (demoData.brandLogo && Array.isArray(demoData.brandLogo)) {
            demoData.brandLogo.forEach((logo: any) => {
                const includeCountries = logo.includeCountryCodes;
                const excludeCountries = logo.excludeCountryCodes;
                if (includeCountries && includeCountries.length > 0 && !includeCountries.includes(countryCode)) {
                    return;
                }
                if (excludeCountries && excludeCountries.length > 0 && excludeCountries.includes(countryCode)) {
                    return;
                }
                setBrandLogoOverride(logo.LogoPath);
                return;
            });
        }
        if (badgeOverrides && Array.isArray(badgeOverrides) && badgeOverrides.length > 0 && demoData.badges && Array.isArray(demoData.badges) && demoData.badges.length > 0) {
            const demoBadgesArray: { icon: string; title: string, code: string, position: string }[] = [];
            demoData.badges.forEach((badge: any) => {
                const includeCountries = badge.includeCountryCodes;
                const excludeCountries = badge.excludeCountryCodes;
                if (includeCountries && includeCountries.length > 0 && !includeCountries.includes(countryCode)) {
                    return;
                }
                if (excludeCountries && excludeCountries.length > 0 && excludeCountries.includes(countryCode)) {
                    return;
                }
                if (badgeOverrides.find((badgeOverride) => badgeOverride.code === badge.badgeId)) {
                    demoBadgesArray.push({
                        icon: badgeOverrides.find((badgeOverride) => badgeOverride.code === badge.badgeId)?.icon || '',
                        title: badgeOverrides.find((badgeOverride) => badgeOverride.code === badge.badgeId)?.title || '',
                        code: badge.badgeId,
                        position: badge.position,
                    });
                }
            });
            setDemoBadges(demoBadgesArray);
        }
        if (demoData.additional_links && Array.isArray(demoData.additional_links) && demoData.additional_links.length > 0) {
            const demoLinksArray: { icon: string; title: string; url: string, position: string }[] = [];
            demoData.additional_links.forEach((link: any) => {
                const includeCountries = link.includeCountryCodes;
                const excludeCountries = link.excludeCountryCodes;
                if (includeCountries && includeCountries.length > 0 && !includeCountries.includes(countryCode)) {
                    return;
                }
                if (excludeCountries && excludeCountries.length > 0 && excludeCountries.includes(countryCode)) {
                    return;
                }
                demoLinksArray.push({
                    icon: link.icon,
                    title: link.value,
                    url: link.link,
                    position: link.position,
                });
            });
            setDemoLinks(demoLinksArray);
        }
        const demoSpecificationsArray: GenericSpecification[] = [];
        if (demoData.tabs && typeof demoData.tabs === 'object' && Object.keys(demoData.tabs).length > 0) {
            for (let i = 0; i < Object.keys(demoData.tabs).length; i++) {
                const tabName = Object.keys(demoData.tabs)[i];
                const tabSpecifications = demoData.tabs[tabName];
                const tabId = toKebabCase(tabName);
                if (tabSpecifications && Array.isArray(tabSpecifications)) {
                    for (let j = 0; j < tabSpecifications.length; j++) {
                        const specification = tabSpecifications[j];
                        const includeCountries = specification.includeCountryCodes;
                        const excludeCountries = specification.excludeCountryCodes;
                        if (includeCountries && includeCountries.length > 0 && !includeCountries.includes(countryCode)) {
                            return;
                        }
                        if (excludeCountries && excludeCountries.length > 0 && excludeCountries.includes(countryCode)) {
                            return;
                        }
                        const sourceRows = Array.isArray(specification.value)
                            && specification.value.length > 0
                            && specification.value.every((entry: any) => entry && typeof entry === 'object' && !entry.isocode)
                            ? specification.value
                            : [specification];
                        for (let k = 0; k < sourceRows.length; k++) {
                            const row = sourceRows[k];
                            const source = row === specification ? specification : { ...specification, ...row };
                            const name = getTranslatableProductField(source, 'name', 'translation_name', '', language);
                            const value = await checkForOutOfLocaleLinks(getTranslatableProductField(source, 'value', 'translation_value', '', language));
                            if (!name || !value) {
                                return;
                            }
                            const tab = getTranslatableProductField(source, 'tab', 'translation_tab', tabName, language);
                            const subtab = getTranslatableProductField(source, 'sub-tab', 'translation_sub-tab', '', language);
                            const subtabId = toKebabCase(source['sub-tab'] || '');
                            const section = getTranslatableProductField(source, 'group', 'translation_group', '', language);
                            const sectionId = toKebabCase(source.group || '');
                            const subsection = getTranslatableProductField(source, 'section', 'translation_section', '', language);
                            const subsectionId = toKebabCase(source.section || '');
                            demoSpecificationsArray.push({
                                tab: tab,
                                tabId: tabId,
                                subtab: subtab,
                                subtabId: subtabId,
                                section: section,
                                sectionId: sectionId,
                                subsection: subsection,
                                subsectionId: subsectionId,
                                cas: source.cas || specification.cas || '',
                                name: name,
                                value: value,
                            });
                        }
                    }
                }
            }
            normalizeDemoSpecifications(demoSpecificationsArray);
            applyDemoSpecificationTabs(demoSpecificationsArray);
            setDemoSpecifications(demoSpecificationsArray);
        }
        if (demoData.description && Array.isArray(demoData.description)) {
            setProductDescriptionOverride(getTranslatableProductField(demoData, 'description', 'description_value', '', language));
        }
        if (demoData.galleryImages && Array.isArray(demoData.galleryImages)) {
            const demoImagesArray: { url: string; alt: string }[] = [];
            demoData.galleryImages.forEach((image: any) => {
                const includeCountries = image.includeCountryCodes;
                const excludeCountries = image.excludeCountryCodes;
                if (includeCountries && includeCountries.length > 0 && !includeCountries.includes(countryCode)) {
                    return;
                }
                if (excludeCountries && excludeCountries.length > 0 && excludeCountries.includes(countryCode)) {
                    return;
                }
                demoImagesArray.push({ url: image.url, alt: image.altText });
            });
            setDemoImages(demoImagesArray);
        }
    };

    const isBlank = (value: string | null | undefined) => value == null || String(value).trim() === '';

    const normalizeDemoSpecifications = (specifications: GenericSpecification[]) => {
        const specsByTab: Record<string, GenericSpecification[]> = {};
        specifications.forEach((specification) => {
            if (!specsByTab[specification.tabId]) {
                specsByTab[specification.tabId] = [];
            }
            specsByTab[specification.tabId].push(specification);
        });
        Object.keys(specsByTab).forEach((tabName) => {
            const tabSpecs = specsByTab[tabName];
            const hasDefinedSubtab = tabSpecs.some((specification) => !isBlank(specification.subtabId));
            tabSpecs.forEach((specification) => {
                if (isBlank(specification.subtabId)) {
                    specification.subtabId = hasDefinedSubtab ? 'general' : '';
                    specification.subtab = hasDefinedSubtab ? 'General' : '';
                }
            });
            const specsBySubtab: Record<string, GenericSpecification[]> = {};
            tabSpecs.forEach((specification) => {
                const subtabKey = specification.subtabId || '';
                if (!specsBySubtab[subtabKey]) {
                    specsBySubtab[subtabKey] = [];
                }
                specsBySubtab[subtabKey].push(specification);
            });
            Object.keys(specsBySubtab).forEach((subtabName) => {
                const subtabSpecs = specsBySubtab[subtabName];
                const hasDefinedSection = subtabSpecs.some((specification) => !isBlank(specification.sectionId));
                subtabSpecs.forEach((specification) => {
                    if (isBlank(specification.sectionId)) {
                        specification.sectionId = hasDefinedSection ? 'other' : '';
                        specification.section = hasDefinedSection ? i18n('Other') : '';
                    }
                });
            });
        });
    };

    const applyDemoSpecificationTabs = (specifications: GenericSpecification[]) => {
        if (specifications.length === 0) {
            return;
        }
        setSectionTabs((currentTabs) => {
            const newSectionTabs = currentTabs.map((tab) => ({
                ...tab,
                subtabs: tab.subtabs.map((subtab) => ({
                    ...subtab,
                    sections: [...subtab.sections],
                    subtabs: [...subtab.subtabs],
                })),
                sections: [...tab.sections],
            }));
            const createTab = (name: string, label: string): TabSection => ({
                name,
                label,
                subtabs: [],
                sections: [],
                activeSection: '',
                hidden: hiddenTabs.includes(label) || hiddenTabs.includes(name),
                collapseSubtabs: false,
            });
            const findTab = (tabs: TabSection[], slug: string) => {
                return tabs.find((tab) => tab.name === slug);
            };

            specifications.forEach((specification) => {
                const tabSlug = specification.tabId;
                let tab = findTab(newSectionTabs, tabSlug);
                if (!tab) {
                    tab = createTab(tabSlug, specification.tab);
                    newSectionTabs.push(tab);
                }
                if (specification.subtab) {
                    const subtabSlug = specification.subtabId;
                    let subtab = findTab(tab.subtabs, subtabSlug);
                    if (!subtab) {
                        subtab = createTab(subtabSlug, specification.subtab);
                        const tabHadExistingSubtabs = (currentTabs.find((currentTab) => currentTab.name === tab.name)?.subtabs.length ?? 0) > 0;
                        if ((specification.subtabId === 'general' || subtabSlug === 'general') && !tabHadExistingSubtabs) {
                            tab.subtabs.unshift(subtab);
                        } else {
                            tab.subtabs.push(subtab);
                        }
                    }
                    if (specification.section && !subtab.sections.find((section) => section.name === specification.sectionId || section.label === specification.section)) {
                        subtab.sections.push({ name: specification.sectionId, label: specification.section });
                    }
                } else if (specification.section && !tab.sections.find((section) => section.name === specification.sectionId || section.label === specification.section)) {
                    tab.sections.push({ name: specification.sectionId, label: specification.section });
                }
            });

            newSectionTabs.sort((a, b) => {
                const aIndex = tabSorting.findIndex((tabSortingItem) => tabSortingItem[a.label]);
                const bIndex = tabSorting.findIndex((tabSortingItem) => tabSortingItem[b.label]);
                return aIndex - bIndex;
            });
            newSectionTabs.forEach((tab) => {
                tabSorting.forEach((tabSortingItem) => {
                    if (tabSortingItem[tab.label]) {
                        tab.subtabs.sort((a, b) => {
                            const aIndex = tabSortingItem[tab.label].indexOf(a.label);
                            const bIndex = tabSortingItem[tab.label].indexOf(b.label);
                            return aIndex - bIndex;
                        });
                    }
                });
            });
            const rawHash = window.location.hash.slice(1);
            if (rawHash) {
                let hash = rawHash;
                try {
                    hash = decodeURIComponent(rawHash);
                } catch (decodeError) {
                    hash = rawHash;
                }
                const hashParts = hash.split('__');
                if (hashParts.length > 2) {
                    const [tabName, subtabName] = hashParts;
                    const sectionName = hashParts.slice(2).join('__');
                    const tab = newSectionTabs.find((item) => item.name === tabName);
                    const subtab = tab?.subtabs.find((item) => item.name === subtabName);
                    if (subtab && (sectionName === 'All' || subtab.sections.some((section) => section.name === sectionName))) {
                        subtab.activeSection = sectionName;
                    }
                } else if (hashParts.length === 2) {
                    const [tabName, hashPart2] = hashParts;
                    const tab = newSectionTabs.find((item) => item.name === tabName);
                    if (tab && !tab.subtabs.some((subtab) => subtab.name === hashPart2) && (hashPart2 === 'All' || tab.sections.some((section) => section.name === hashPart2))) {
                        tab.activeSection = hashPart2;
                    }
                }
            }
            return newSectionTabs;
        });
    };

    const checkForOutOfLocaleLinks = async (value: string) => {
        let newValue = value;
        const valueLinks = new DOMParser().parseFromString(value, 'text/html').querySelectorAll('a');
        for (let i = 0; i < valueLinks.length; i++) {
            try {
                const linkHref = valueLinks[i].href;
                if (linkHref.includes('rockwellautomation.com/')) {
                    const linkLocale = linkHref.split('rockwellautomation.com/')[1].split('/')[0].replace('.html', '');
                    if (!linkLocale) {
                        continue;
                    }
                    if (linkLocale !== locale) {
                        let newHref = linkHref.replace(linkLocale, locale);
                        if (newHref.includes('/zh-cn/')) {
                            newHref = newHref.replace('/zh-cn/', '.cn');
                        }
                        const newHrefResponse = await fetch(newHref, { method: 'HEAD'});
                        if (newHrefResponse.status >= 200 && newHrefResponse.status < 300) {
                            newValue = newValue.replace(linkHref, newHref);
                        }
                    }
                }
            } catch (error) {
                console.warn('Error checking for out of locale links:', error);
            }
        }
        return newValue;
    };

    const checkForOutOfLocaleXf = async (xfPath: string) => {
        try {
            if (xfPath.includes('/content/experience-fragments/rockwell-automation/global/')) {
                let linkRegionLanguage = '';
                const linkRegion = xfPath.split('/content/experience-fragments/rockwell-automation/global/')[1].split('/')[0];
                if (linkRegion === 'en') {
                    linkRegionLanguage = 'en';
                } else if (linkRegion) {
                    const linkLanguage = xfPath.split(`/content/experience-fragments/rockwell-automation/global/${linkRegion}/`)[1].split('/')[0];
                    if (linkLanguage) {
                        linkRegionLanguage = `${linkRegion}/${linkLanguage}`;
                    }
                }
                if (!linkRegionLanguage) {
                    return xfPath;
                }
                const newXfPath = xfPath.replace(`/${linkRegionLanguage}`, `/${regionLanguage}`);
                if (linkRegionLanguage !== regionLanguage) {
                    const newHrefResponse = await fetch(`${newXfPath}.html`, { method: 'HEAD'});
                    if (newHrefResponse.status >= 200 && newHrefResponse.status < 300) {
                        return newXfPath;
                    }
                }
            }
        } catch (error) {
            console.warn('Error checking for out of locale XF:', error);
        }
        return xfPath;
    };

    const initI18n = async () => {
        try {
            const lang = language === 'zh' ? 'zh-cn' : (language === 'ko' ? 'ko-kr' : language.toLowerCase());
            const i18nDictionaryResponse = await fetch(`/libs/cq/i18n/dict.${lang}.json`);
            const i18nDictionaryJson = await i18nDictionaryResponse.json();
            // Ref so the dictionary survives re-renders and is readable in the
            // same async tick after await (unlike setState, which is deferred).
            i18nDictionaryRef.current = i18nDictionaryJson;
        } catch (error) {
            console.warn(`Error initializing i18n for language ${language}:`, error);
        } finally {
            setI18nReady(true);
        }
    };

    const i18n = (key: string) => {
        return i18nDictionaryRef.current[key] || key;
    };

    const redirectToHardwarePage = () => {
        const path = window.location.pathname;

        // Preferred locale from the langSite cookie, e.g. "/pt-br.html" -> "pt-br".
        const langSite = getCookieValue('langSite');
        const preferredLocale = langSite ? decodeURIComponent(langSite).replace(/^\/(.+)\.html$/, '$1').toLowerCase() : 'en-us';
        let hardwareUrl = `${window.location.origin}/${preferredLocale}/products/hardware.html`;
        // zh-cn is served from the .com.cn domain without a locale prefix, so
        // swapping in "/zh-cn" collapses to ".com.cn" (matches HrefLang externalize).
        if (preferredLocale === 'zh-cn') {
            hardwareUrl = hardwareUrl.replace('.com/zh-cn', '.com.cn');
        }

        window.location.href = hardwareUrl;
    };

    const emailProduct = () => {
        const uri = document.querySelector('link[rel="canonical"]')?.getAttribute('href');
        const etoId = `Engineered-to-Order Product: ${proxy || etoProduct?.name}`;
        const brand = 'Rockwell Automation';
        const body = etoId + ' from ' + brand + '\n' + uri + '\n';
        window.location.href = 'mailto:?subject=' + encodeURIComponent(brand + ' ' + etoId) + '&body=' + encodeURIComponent(body);
    };

    const generateQrCode = () => {
        const qrCodeImg = document.querySelector(`.${componentName}__qr-code-dialog-qr-code`);
        try {
            new QRCode(qrCodeImg, {
                text: window.location.href,
                width: 220,
                height: 220,
                colorDark: '#000000',
                colorLight: '#ffffff',
                correctLevel: QRCode.CorrectLevel.H
            });
        } catch (error) {
            console.warn('Error generating QR code:', error);
        }
    };

    const printQrCode = () => {
        const qrCodeDialog = document.querySelector(`.${componentName}__qr-code-dialog`);
        const qrModalStyles = document.createElement('style');
        qrModalStyles.innerHTML = `
            .${componentName}__qr-code-dialog-card {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 1rem;
                padding: 26px 40px;
                margin: auto;
                max-width: 100em;
            }
            .${componentName}__qr-code-dialog-body {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 1rem;
                margin: auto;
            }
            .${componentName}__qr-code-dialog-title {
                color: #2D2D2D;
                font-family: 'BarlowMedium', 'Barlow', sans-serif;
                font-size: 24px;
                line-height: 28px;
            }
            .${componentName}__qr-code-dialog-description {
                color: #2D2D2D;
                font-family: 'BarlowRegular', 'Barlow', sans-serif;
                font-size: 16px;
                line-height: 22px;
            }
            .${componentName}__qr-code-dialog-button-container, .${componentName}__qr-code-dialog-x-button {
                display: none;
            }
        `;
        const printWindow = window.open('', '_blank');
        if (printWindow && qrCodeDialog) {
            printWindow.document.write(`
                <html>
                    <head>
                        <title>QR Code</title>
                    </head>
                    <body>
                        ${qrCodeDialog.innerHTML}
                    </body>
                </html>
            `);
            const sourceCanvas = qrCodeDialog.querySelector('canvas');
            const printWindowCanvas = printWindow.document.querySelector('canvas');
            if (printWindowCanvas && sourceCanvas) {
                const printWindowContext = printWindowCanvas.getContext('2d');
                if (printWindowContext) {
                    printWindowContext.drawImage(sourceCanvas, 0, 0);
                }
            }
            printWindow.document.head.append(qrModalStyles);
            printWindow.document.close();
            printWindow.onload = () => {
                printWindow.print();
            }
        }
    };

    const getCookieValue = (name: string) => {
        const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '\\s*=\\s*([^;]+)'));
        return match ? match[1] : null;
    };

    const updateUrlParams = () => {
        const url = new URL(window.location.href);
        if (activeSection?.startsWith(sectionSelectors.components)) {
            url.searchParams.set('pageNumber', pageNumber.toString());
            url.searchParams.set('pageSize', pageSize.toString());
        } else {
            url.searchParams.delete('pageNumber');
            url.searchParams.delete('pageSize');
        }
        window.history.pushState({}, '', url);
    };

    const updateUrlHash = () => {
        const url = new URL(window.location.href);
        let urlHash = url.hash;
        const [tabName, subtabName] = activeSection.split('__');
        const tab = sectionTabs.find((tab) => tab.name === tabName);
        if (tab) {
            if (subtabName) {
                const subtab = tab.subtabs.find((subtab) => subtab.name === subtabName);
                if (subtab) {
                    urlHash = subtab.activeSection ? (tabName + '__' + subtabName + '__' + subtab.activeSection) : (tabName + '__' + subtabName);
                }
            } else {
                urlHash = tab.activeSection ? (tabName + '__' + tab.activeSection) : tabName;
            }
        }
        url.hash = urlHash;
        window.history.pushState({}, '', url.toString());
    };

    const readUrlHash = () => {
        const url = new URL(window.location.href);
        const rawHash = url.hash.slice(1);
        let hash = rawHash;
        try {
            hash = decodeURIComponent(rawHash);
        } catch (decodeError) {
            hash = rawHash;
        }
        if (hash) {
            const hashParts = hash.split('__');
            if (hashParts.length > 2) {
                const [tabName, subtabName] = hashParts;
                const subsectionName = hashParts.slice(2).join('__');
                if (!visibleSections.find((tab) => tab.name === tabName)) {
                    return;
                }
                if (!visibleSections.find((tab) => tab.name === tabName)?.subtabs.find((subtab) => subtab.name === subtabName)) {
                    return;
                }
                setActiveSection(tabName + '__' + subtabName);
                setActiveSubsection(tabName, subtabName, subsectionName);
            } else if (hashParts.length > 1) {
                const [tabName, hashPart2] = hashParts;
                const subtab = sectionTabs.find((tab) => tab.name === tabName)?.subtabs.find((subtab) => subtab.name === hashPart2);
                const section = sectionTabs.find((tab) => tab.name === tabName)?.sections.find((section) => section.name === hashPart2);
                if (subtab) {
                    if (!visibleSections.find((tab) => tab.name === tabName)) {
                        return;
                    }
                    if (!visibleSections.find((tab) => tab.name === tabName)?.subtabs.find((st) => st.name === subtab.name)) {
                        return;
                    }
                    setActiveSection(tabName + '__' + subtab.name);
                } else if (section) {
                    if (!visibleSections.find((tab) => tab.name === tabName)) {
                        return;
                    }
                    setActiveSection(tabName);
                    setActiveSubsection(tabName, null, section.name);
                }
            } else if (hashParts.length > 0) {
                const [tabName] = hashParts;
                if (!visibleSections.find((tab) => tab.name === tabName)) {
                    return;
                }
                setActiveSection(tabName);
            }
        }
    };

    const setActiveSubsection = (tabName: string, subtabName: string | null, subsectionName: string) => {
        const newSectionTabs = [...sectionTabs];
        const tab = newSectionTabs.find((tab) => tab.name === tabName);
        if (tab) {
            if (subtabName) {
                const subtab = tab.subtabs.find((subtab) => subtab.name === subtabName);
                if (subtab) {
                    subtab.activeSection = subsectionName;
                }
            } else {
                tab.activeSection = subsectionName;
            }
        }
        setSectionTabs(newSectionTabs);
    };

    const hideContactDialog = () => {
        setSelectedDistributor(null);
        const dialog = document.querySelector<HTMLDialogElement>(`.${componentName}__contact-dialog`);
        if (dialog) {
            dialog.close();
        }
    };

    const hideQrDialog = () => {
        const dialog = document.querySelector<HTMLDialogElement>(`.${componentName}__qr-code-dialog`);
        if (dialog) {
            dialog.close();
        }
    };

    const getTranslatableProductField = (productJson: any, field: string, flattenedField: string, defaultValue: string, language: string) => {
        const languageCode = String(language || 'en').toLowerCase().replace(/_/g, '-');
        const matchesLanguage = (isocode: string, requested: string) => {
            const code = String(isocode || '').toLowerCase().replace(/_/g, '-');
            if (!code || !requested) {
                return false;
            }
            return code === requested || code.startsWith(`${requested}-`) || requested.startsWith(`${code}-`);
        };
        const readEntryValue = (entry: any) => {
            if (!entry || typeof entry !== 'object') {
                return '';
            }
            if (typeof entry.value === 'string' || typeof entry.value === 'number') {
                return String(entry.value);
            }
            return '';
        };
        const readTranslationArray = (candidate: any, requested: string) => {
            if (!Array.isArray(candidate)) {
                return '';
            }
            try {
                const entries = candidate.filter((entry) => entry && typeof entry === 'object' && entry.isocode);
                const match = entries.find((entry) => matchesLanguage(entry.isocode, requested))
                    || entries.find((entry) => matchesLanguage(entry.isocode, 'en'));
                return readEntryValue(match);
            } catch (error) {
                console.warn(`Error getting translatable product field ${field} for language ${language}:`, error);
                return '';
            }
        };
        const readPlainText = (candidate: any) => {
            if (typeof candidate === 'string' || typeof candidate === 'number') {
                return String(candidate);
            }
            if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
                return readEntryValue(candidate);
            }
            return '';
        };
        return readTranslationArray(productJson?.[field], languageCode)
            || readTranslationArray(productJson?.[flattenedField], languageCode)
            || readPlainText(productJson?.[field])
            || readPlainText(productJson?.[flattenedField])
            || defaultValue;
    };

    const generateProductLink = (catalogNumber: string) => {
        return `${window.location.origin}${locale === 'zh-cn' ? '' : `/${locale}`}/products/details.${catalogNumber}.html`;
    };

    const toTitleCase = (str: string) => {
        return str.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    };

    const toKebabCase = (value: string) => {
        return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    };

    const getComponentLabel = (componentCode: string) => {
        return components.find((component) => component.id === componentCode)?.name || componentCode;
    };

    const fetchData = async () => {
        setComponentsLoading(true);
        try {
            await getEtoInfo();
            setError(null);
        } catch (error) {
            console.warn(error);
            setError('Failed to get components');
            redirectToHardwarePage();
        } finally {
            setComponentsLoading(false);
        }
    };

    const initEtoApp = async () => {
        await initI18n();
        await fetchData();
        setActiveSection(initialSection);
        generateQrCode();
        readUrlHash();
    };

    useEffect(() => {
        initEtoApp();
    }, []);

    useEffect(() => {
        updateUrlParams();
        setTotalPages(Math.ceil(components.length / pageSize));
    }, [pageSize, pageNumber, activeSection]);

    useEffect(() => {
        updateUrlHash();
    }, [activeSection]);

    if (error) {
        return <div>Error: {error}</div>;
    }

    // Wait for the dictionary so the first paint (and any i18n calls in
    // fetchData after await initI18n) never runs against an empty map.
    if (!i18nReady) {
        return null;
    }

    const [activeTabName, activeSubtabName] = (activeSection || '').split('__');
    const matchingDemoSpecifications = (demoSpecifications || []).filter((specification) => {
        const specTab = specification.tabId;
        if (specTab !== activeTabName) {
            return false;
        }
        const specSubtab = specification.subtabId;
        if (!activeSubtabName) {
            return !specSubtab;
        }
        return specSubtab === activeSubtabName;
    });
    const isBuiltInActiveTab = [
        sectionSelectors.overview,
        sectionSelectors.components,
        sectionSelectors.technicalSpecifications,
        sectionSelectors.documents,
        sectionSelectors.certifications,
    ].some((name) => activeSection === name || activeSection.startsWith(`${name}__`));
    const matchingDemoInsertFragment = insertFragments.find((fragment) => {
        if (fragment.tab !== activeTabName) {
            return false;
        }
        return (!fragment.subtab || !activeSubtabName || fragment.subtab === activeSubtabName);
    });
    const isComponentLevelActive = activeSection === sectionSelectors.technicalSpecifications + '__' + sectionSelectors.componentLevelSpecifications;
    const showDemoInsertFragment = Boolean(matchingDemoInsertFragment?.contentPath) && (!isBuiltInActiveTab || (activeSection.startsWith(sectionSelectors.technicalSpecifications) && !isComponentLevelActive));
    const demoSpecSection = matchingDemoSpecifications.length > 0 ? (
        <GenericSpecificationSection componentName={componentName} componentsLoading={componentsLoading} error={error} specifications={matchingDemoSpecifications} sectionTabs={sectionTabs} activeTabName={activeTabName} activeSubtabName={activeSubtabName} setActiveSubsection={setActiveSubsection} updateUrlHash={updateUrlHash} i18n={i18n} />
    ) : null;

    return <div className={`${componentName}__container`} data-last-updated={etoProduct?.lastUpdated?.toISOString()}>
        <EtoHeader componentName={componentName} mobileView={mobileView} locale={locale} brandIcon={brandIcon} brandLogoOverride={brandLogoOverride} badges={demoBadges} links={demoLinks} proxy={proxy} serialNumber={serialNumber} serialNumberTooltipText={serialNumberTooltipText} etoProduct={etoProduct} activeTooltip={activeTooltip} productDescriptionOverride={productDescriptionOverride} demoImages={demoImages} setActiveTooltip={setActiveTooltip} setSelectedDistributor={setSelectedDistributor} emailProduct={emailProduct} i18n={i18n} />
        <div className={`${componentName}__header`}>
            <h1 className={`${componentName}__title`}>{i18n('Product Details')}</h1>
        </div>
        {desktopView && (
            <div className={`${componentName}__section-container ${componentName}__section-container--desktop`}>
                <div className={`${componentName}__section-tabs`} role="tablist" aria-orientation="vertical">
                    {componentsLoading &&
                        <>
                            {new Array(sectionTabs.filter((tab) => (!tab.hidden && (tab.subtabs.length === 0 || tab.subtabs.some((subtab) => !subtab.hidden)))).length).fill(0).map((_, index) => (
                                <div key={index} className={`${componentName}__section-tab ${componentName}__section-tab-skeleton ${componentName}__loading-animation`}></div>
                            ))}
                        </>
                    }
                    {!componentsLoading &&
                        <EtoTabs componentName={componentName} sectionTabs={sectionTabs} setSectionTabs={setSectionTabs} activeSection={activeSection} setActiveSection={setActiveSection} i18n={i18n} />
                    }
                </div>
                {activeSection === sectionSelectors.overview && !sectionTabs.find((tab) => tab.name === sectionSelectors.overview)?.hidden && (
                    <div className={`${componentName}__section`}>
                        {insertFragments.find((fragment) => fragment.tab === sectionSelectors.overview)?.position === 'top' && insertFragments.find((fragment) => fragment.tab === sectionSelectors.overview)?.contentPath && (
                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => fragment.tab === sectionSelectors.overview)?.contentPath} />
                        )}
                        <OverviewSection proxy={proxy || ''} componentName={componentName} componentsLoading={componentsLoading} error={error} etoProduct={etoProduct} serialNumber={serialNumber} euImporterAddress={euImporterAddress || ''} euRepresentativeAddress={euRepresentativeAddress || ''} i18n={i18n} />
                        {demoSpecSection}
                        {insertFragments.find((fragment) => fragment.tab === sectionSelectors.overview)?.position === 'bottom' && insertFragments.find((fragment) => fragment.tab === sectionSelectors.overview)?.contentPath && (
                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => fragment.tab === sectionSelectors.overview)?.contentPath} />
                        )}
                    </div>
                )}
                {activeSection === sectionSelectors.components && (componentsLoading || components.length > 0) && !sectionTabs.find((tab) => tab.name === sectionSelectors.components)?.hidden && (
                    <div className={`${componentName}__section`}>
                        {insertFragments.find((fragment) => fragment.tab === sectionSelectors.components)?.position === 'top' && insertFragments.find((fragment) => fragment.tab === sectionSelectors.components)?.contentPath && (
                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => fragment.tab === sectionSelectors.components)?.contentPath} />
                        )}
                        <ComponentsSection componentName={componentName} serialNumber={serialNumber} componentsLoading={componentsLoading} error={error} components={components} pageNumber={pageNumber} setPageNumber={setPageNumber} pageSize={pageSize} setPageSize={setPageSize} totalPages={totalPages} toTitleCase={toTitleCase} i18n={i18n} />
                        {demoSpecSection}
                        {insertFragments.find((fragment) => fragment.tab === sectionSelectors.components)?.position === 'bottom' && insertFragments.find((fragment) => fragment.tab === sectionSelectors.components)?.contentPath && (
                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => fragment.tab === sectionSelectors.components)?.contentPath} />
                        )}
                    </div>
                )}
                {activeSection.startsWith(sectionSelectors.technicalSpecifications) && (componentsLoading || componentSpecifications || matchingDemoSpecifications.length > 0) && !sectionTabs.find((tab) => tab.name === sectionSelectors.technicalSpecifications)?.hidden && (
                    <div className={`${componentName}__section`}>
                        {activeSection === sectionSelectors.technicalSpecifications + '__' + sectionSelectors.componentLevelSpecifications && (
                            <>
                                {insertFragments.find((fragment) => (fragment.tab === sectionSelectors.technicalSpecifications && fragment.subtab === sectionSelectors.componentLevelSpecifications))?.position === 'top' && insertFragments.find((fragment) => (fragment.tab === sectionSelectors.technicalSpecifications && fragment.subtab === sectionSelectors.componentLevelSpecifications))?.contentPath && (
                                    <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => (fragment.tab === sectionSelectors.technicalSpecifications && fragment.subtab === sectionSelectors.componentLevelSpecifications))?.contentPath} />
                                )}
                                <ComponentLevelSpecificationsSection componentName={componentName} componentsLoading={componentsLoading} error={error} componentSpecifications={componentSpecifications} sectionTabs={sectionTabs} sectionSelectors={sectionSelectors} setActiveSubsection={setActiveSubsection} updateUrlHash={updateUrlHash} getComponentLabel={getComponentLabel} euImporterAddress={euImporterAddress || ''} euRepresentativeAddress={euRepresentativeAddress || ''} i18n={i18n} />
                                {insertFragments.find((fragment) => (fragment.tab === sectionSelectors.technicalSpecifications && fragment.subtab === sectionSelectors.componentLevelSpecifications))?.position === 'bottom' && insertFragments.find((fragment) => (fragment.tab === sectionSelectors.technicalSpecifications && fragment.subtab === sectionSelectors.componentLevelSpecifications))?.contentPath && (
                                    <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => (fragment.tab === sectionSelectors.technicalSpecifications && fragment.subtab === sectionSelectors.componentLevelSpecifications))?.contentPath} />
                                )}
                            </>
                        )}
                        {activeSection !== sectionSelectors.technicalSpecifications + '__' + sectionSelectors.componentLevelSpecifications && matchingDemoInsertFragment?.position === 'top' && matchingDemoInsertFragment.contentPath && (
                            <InsertFragment componentName={componentName} contentPath={matchingDemoInsertFragment.contentPath} />
                        )}
                        {demoSpecSection}
                        {activeSection !== sectionSelectors.technicalSpecifications + '__' + sectionSelectors.componentLevelSpecifications && matchingDemoInsertFragment?.position === 'bottom' && matchingDemoInsertFragment.contentPath && (
                            <InsertFragment componentName={componentName} contentPath={matchingDemoInsertFragment.contentPath} />
                        )}
                    </div>
                )}
                {activeSection === sectionSelectors.documents && (componentsLoading || documents) && Object.keys(documents).length > 0 && !sectionTabs.find((tab) => tab.name === sectionSelectors.documents)?.hidden && (
                    <div className={`${componentName}__section`}>
                        {insertFragments.find((fragment) => fragment.tab === sectionSelectors.documents)?.position === 'top' && insertFragments.find((fragment) => fragment.tab === sectionSelectors.documents)?.contentPath && (
                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => fragment.tab === sectionSelectors.documents)?.contentPath} />
                        )}
                        <DocumentSection componentName={componentName} componentsLoading={componentsLoading} error={error} documents={documents} components={components} tab={sectionTabs.find((tab) => tab.name === sectionSelectors.documents)} setActiveSubsection={setActiveSubsection} i18n={i18n} />
                        {demoSpecSection}
                        {insertFragments.find((fragment) => fragment.tab === sectionSelectors.documents)?.position === 'bottom' && insertFragments.find((fragment) => fragment.tab === sectionSelectors.documents)?.contentPath && (
                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => fragment.tab === sectionSelectors.documents)?.contentPath} />
                        )}
                    </div>
                )}
                {activeSection === sectionSelectors.certifications && (componentsLoading || certifications) && Object.keys(certifications).length > 0 && !sectionTabs.find((tab) => tab.name === sectionSelectors.certifications)?.hidden && (
                    <div className={`${componentName}__section`}>
                        {insertFragments.find((fragment) => fragment.tab === sectionSelectors.certifications)?.position === 'top' && insertFragments.find((fragment) => fragment.tab === sectionSelectors.certifications)?.contentPath && (
                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => fragment.tab === sectionSelectors.certifications)?.contentPath} />
                        )}
                        <CertificationSection componentName={componentName} componentsLoading={componentsLoading} error={error} certifications={certifications} components={components} tab={sectionTabs.find((tab) => tab.name === sectionSelectors.certifications)} setActiveSubsection={setActiveSubsection} euImporterAddress={euImporterAddress || ''} euRepresentativeAddress={euRepresentativeAddress || ''} i18n={i18n} />
                        {demoSpecSection}
                        {insertFragments.find((fragment) => fragment.tab === sectionSelectors.certifications)?.position === 'bottom' && insertFragments.find((fragment) => fragment.tab === sectionSelectors.certifications)?.contentPath && (
                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => fragment.tab === sectionSelectors.certifications)?.contentPath} />
                        )}
                    </div>
                )}
                {!isBuiltInActiveTab && matchingDemoSpecifications.length > 0 && !sectionTabs.find((tab) => tab.name === activeTabName)?.hidden && (
                    <div className={`${componentName}__section`}>
                        {matchingDemoInsertFragment?.position === 'top' && matchingDemoInsertFragment.contentPath && (
                            <InsertFragment componentName={componentName} contentPath={matchingDemoInsertFragment.contentPath} />
                        )}
                        {demoSpecSection}
                        {matchingDemoInsertFragment?.position === 'bottom' && matchingDemoInsertFragment.contentPath && (
                            <InsertFragment componentName={componentName} contentPath={matchingDemoInsertFragment.contentPath} />
                        )}
                    </div>
                )}
            </div>
        )}
        {!desktopView && (
            <div className={`${componentName}__section-container ${componentName}__section-container--mobile`}>
                {componentsLoading &&
                    <>
                        {new Array(sectionTabs.filter((tab) => (!tab.hidden && (tab.subtabs.length === 0 || tab.subtabs.some((subtab) => !subtab.hidden)))).length).fill(0).map((_, index) => (
                            <div key={index} className={`${componentName}__section-details ${componentName}__section-details-skeleton ${componentName}__loading-animation`}></div>
                        ))}
                    </>
                }
                {!componentsLoading && sectionTabs.filter((tab) => (!tab.hidden && (tab.subtabs.length === 0 || tab.subtabs.some((subtab) => !subtab.hidden)))).map((tab) => (
                    <>
                        <details
                            key={tab.name}
                            className={`${componentName}__section-details ${activeSection.startsWith(tab.name) ? 'active' : ''}`}
                            open={activeSection.startsWith(tab.name)}
                        >
                            <summary
                                className={`${componentName}__section-details-summary`}
                                onClick={(event) => {
                                    event.preventDefault();
                                    const target = tab.subtabs.length > 0 ? tab.name + '__' + tab.subtabs[0].name : tab.name;
                                    setActiveSection(activeSection.startsWith(tab.name) ? '' : target);
                                }}
                            >
                                <span className={`${componentName}__section-details-summary-label`}>{i18n(tab.label)}</span>
                                <svg className={`${componentName}__section-details-chevron`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                    <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </summary>
                            {tab.subtabs.length > 0 && activeSection.startsWith(tab.name) && (
                                <div className={`${componentName}__section-details-subtab-selector`}>
                                    <CustomSelect componentName={componentName} value={activeSection.split('__')[1] || tab.subtabs[0].name} options={tab.subtabs.map((subtab) => ({ value: subtab.name, label: subtab.label }))} onChange={(value) => setActiveSection(tab.name + '__' + value)} className={`${componentName}__section-details-subtab-selector-select`} ariaLabel={i18n('Select a section')} i18n={i18n} />
                                </div>
                            )}
                            <div className={`${componentName}__section-details-content`}>
                                {activeSection === sectionSelectors.overview && !sectionTabs.find((tab) => tab.name === sectionSelectors.overview)?.hidden && (
                                    <>
                                        {insertFragments.find((fragment) => fragment.tab === sectionSelectors.overview)?.position === 'top' && insertFragments.find((fragment) => fragment.tab === sectionSelectors.overview)?.contentPath && (
                                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => fragment.tab === sectionSelectors.overview)?.contentPath} />
                                        )}
                                        <OverviewSection proxy={proxy || ''} componentName={componentName} componentsLoading={componentsLoading} error={error} etoProduct={etoProduct} serialNumber={serialNumber} euImporterAddress={euImporterAddress || ''} euRepresentativeAddress={euRepresentativeAddress || ''} i18n={i18n} />
                                        {insertFragments.find((fragment) => fragment.tab === sectionSelectors.overview)?.position === 'bottom' && insertFragments.find((fragment) => fragment.tab === sectionSelectors.overview)?.contentPath && (
                                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => fragment.tab === sectionSelectors.overview)?.contentPath} />
                                        )}
                                    </>
                                )}
                                {activeSection === sectionSelectors.components && (componentsLoading || components.length > 0) && !sectionTabs.find((tab) => tab.name === sectionSelectors.components)?.hidden && (
                                    <>
                                        {insertFragments.find((fragment) => fragment.tab === sectionSelectors.components)?.position === 'top' && insertFragments.find((fragment) => fragment.tab === sectionSelectors.components)?.contentPath && (
                                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => fragment.tab === sectionSelectors.components)?.contentPath} />
                                        )}
                                        <ComponentsSection componentName={componentName} serialNumber={serialNumber} componentsLoading={componentsLoading} error={error} components={components} pageNumber={pageNumber} setPageNumber={setPageNumber} pageSize={pageSize} setPageSize={setPageSize} totalPages={totalPages} toTitleCase={toTitleCase} i18n={i18n} />
                                        {insertFragments.find((fragment) => fragment.tab === sectionSelectors.components)?.position === 'bottom' && insertFragments.find((fragment) => fragment.tab === sectionSelectors.components)?.contentPath && (
                                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => fragment.tab === sectionSelectors.components)?.contentPath} />
                                        )}
                                    </>
                                )}
                                {activeSection === sectionSelectors.technicalSpecifications + '__' + sectionSelectors.componentLevelSpecifications && (componentsLoading || componentSpecifications) && !sectionTabs.find((tab) => tab.name === sectionSelectors.technicalSpecifications)?.hidden && (
                                    <>
                                        {insertFragments.find((fragment) => (fragment.tab === sectionSelectors.technicalSpecifications && fragment.subtab === sectionSelectors.componentLevelSpecifications))?.position === 'top' && insertFragments.find((fragment) => (fragment.tab === sectionSelectors.technicalSpecifications && fragment.subtab === sectionSelectors.componentLevelSpecifications))?.contentPath && (
                                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => (fragment.tab === sectionSelectors.technicalSpecifications && fragment.subtab === sectionSelectors.componentLevelSpecifications))?.contentPath} />
                                        )}
                                        <ComponentLevelSpecificationsSection componentName={componentName} componentsLoading={componentsLoading} error={error} componentSpecifications={componentSpecifications} sectionTabs={sectionTabs} sectionSelectors={sectionSelectors} setActiveSubsection={setActiveSubsection} updateUrlHash={updateUrlHash} getComponentLabel={getComponentLabel} euImporterAddress={euImporterAddress || ''} euRepresentativeAddress={euRepresentativeAddress || ''} i18n={i18n} />
                                        {insertFragments.find((fragment) => (fragment.tab === sectionSelectors.technicalSpecifications && fragment.subtab === sectionSelectors.componentLevelSpecifications))?.position === 'bottom' && insertFragments.find((fragment) => (fragment.tab === sectionSelectors.technicalSpecifications && fragment.subtab === sectionSelectors.componentLevelSpecifications))?.contentPath && (
                                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => (fragment.tab === sectionSelectors.technicalSpecifications && fragment.subtab === sectionSelectors.componentLevelSpecifications))?.contentPath} />
                                        )}
                                    </>
                                )}
                                {activeSection === sectionSelectors.documents && (componentsLoading || documents) && Object.keys(documents).length > 0 && !sectionTabs.find((tab) => tab.name === sectionSelectors.documents)?.hidden && (
                                    <>
                                        {insertFragments.find((fragment) => fragment.tab === sectionSelectors.documents)?.position === 'top' && insertFragments.find((fragment) => fragment.tab === sectionSelectors.documents)?.contentPath && (
                                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => fragment.tab === sectionSelectors.documents)?.contentPath} />
                                        )}
                                        <DocumentSection componentName={componentName} componentsLoading={componentsLoading} error={error} documents={documents} components={components} tab={sectionTabs.find((tab) => tab.name === sectionSelectors.documents)} setActiveSubsection={setActiveSubsection} i18n={i18n} />
                                        {insertFragments.find((fragment) => fragment.tab === sectionSelectors.documents)?.position === 'bottom' && insertFragments.find((fragment) => fragment.tab === sectionSelectors.documents)?.contentPath && (
                                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => fragment.tab === sectionSelectors.documents)?.contentPath} />
                                        )}
                                    </>
                                )}
                                {activeSection === sectionSelectors.certifications && (componentsLoading || certifications) && Object.keys(certifications).length > 0 && !sectionTabs.find((tab) => tab.name === sectionSelectors.certifications)?.hidden && (
                                    <>
                                        {insertFragments.find((fragment) => fragment.tab === sectionSelectors.certifications)?.position === 'top' && insertFragments.find((fragment) => fragment.tab === sectionSelectors.certifications)?.contentPath && (
                                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => fragment.tab === sectionSelectors.certifications)?.contentPath} />
                                        )}
                                        <CertificationSection componentName={componentName} componentsLoading={componentsLoading} error={error} certifications={certifications} components={components} tab={sectionTabs.find((tab) => tab.name === sectionSelectors.certifications)} setActiveSubsection={setActiveSubsection} euImporterAddress={euImporterAddress || ''} euRepresentativeAddress={euRepresentativeAddress || ''} i18n={i18n} />
                                        {insertFragments.find((fragment) => fragment.tab === sectionSelectors.certifications)?.position === 'bottom' && insertFragments.find((fragment) => fragment.tab === sectionSelectors.certifications)?.contentPath && (
                                            <InsertFragment componentName={componentName} contentPath={insertFragments.find((fragment) => fragment.tab === sectionSelectors.certifications)?.contentPath} />
                                        )}
                                    </>
                                )}
                                {showDemoInsertFragment && matchingDemoInsertFragment?.position === 'top' && matchingDemoInsertFragment.contentPath && (
                                    <InsertFragment componentName={componentName} contentPath={matchingDemoInsertFragment.contentPath} />
                                )}
                                {demoSpecSection}
                                {showDemoInsertFragment && matchingDemoInsertFragment?.position === 'bottom' && matchingDemoInsertFragment.contentPath && (
                                    <InsertFragment componentName={componentName} contentPath={matchingDemoInsertFragment.contentPath} />
                                )}
                            </div>
                        </details>
                    </>
                ))}
            </div>
        )}
        {hasAfDemoData && afDemoDisclaimerText && (
            <div className={`${componentName}__af-demo-disclaimer`}>
                <div className={`${componentName}__af-demo-disclaimer-content`} dangerouslySetInnerHTML={{ __html: afDemoDisclaimerText }}></div>
            </div>
        )}
        <dialog className={`${componentName}__contact-dialog`}>
            <form className={`${componentName}__contact-dialog-card`} method="dialog">
                <button className={`${componentName}__contact-dialog-x-button`} aria-label={i18n('Close')}>&#x2715;</button>
                <div className={`${componentName}__contact-dialog-title`}>{selectedDistributor?.partnerName}</div>
                <div className={`${componentName}__contact-dialog-body`}>
                    <span>{i18n('Phone')}:</span><span className={`${componentName}__contact-dialog-number`}>{selectedDistributor?.partnerPhone}</span>
                </div>
                <div className={`${componentName}__contact-dialog-button-container`}>
                    <button className={`${componentName}__contact-dialog-close-button`} onClick={hideContactDialog} tabIndex={0}>{i18n('Close')}</button>
                    <a className={`${componentName}__contact-dialog-call-button`} href={`tel:${selectedDistributor?.partnerPhone}`}>{i18n('Call')}</a>
                </div>
            </form>
        </dialog>
        <dialog className={`${componentName}__qr-code-dialog`}>
            <form className={`${componentName}__qr-code-dialog-card`} method="dialog">
                <button className={`${componentName}__qr-code-dialog-x-button`} aria-label={i18n('Close')}>&#x2715;</button>
                <div className={`${componentName}__qr-code-dialog-title`}>{proxy || etoProduct?.name}</div>
                <div className={`${componentName}__qr-code-dialog-body`}>
                    <div className={`${componentName}__qr-code-dialog-description`}>
                        {productDescriptionOverride || etoProduct?.description}
                    </div>
                    <div className={`${componentName}__qr-code-dialog-qr-code`}></div>
                </div>
                <div className={`${componentName}__qr-code-dialog-button-container`}>
                    <button className={`${componentName}__qr-code-dialog-close-button`} onClick={hideQrDialog}>{i18n('Close')}</button>
                    <button className={`${componentName}__qr-code-dialog-print-button`} onClick={printQrCode}>{i18n('Print')}</button>
                </div>
            </form>
        </dialog>
    </div>;
}

function init(): void {
  const mountPoints = document.querySelectorAll<HTMLElement>(`.${componentName}[data-cmp-is="${componentName}"]`);

  mountPoints.forEach((element) => {
    render(<EtoApp />, element);
  });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
